import axios from 'axios'
import { stashCurrentPathForLoginReturn } from './authReturnPath'
import { isPublicAppRoute } from './publicAppRoutes'
import { isBillingGateApiError } from './featureBillingGate'
import { friendlyApiErrorMessageForCode, normalizeApiErrorMessage } from './apiErrors'

const RAW_BASE_URL = process.env.REACT_APP_API_BASE_URL || ''
const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1'

export function isLocalhostHost(host) {
    return /^(localhost|127\.0\.0\.1)$/i.test(String(host || '').trim())
}

function isLocalApiBaseUrl(url) {
    const s = String(url || '').trim()
    if (!s) return false
    try {
        const parsed = new URL(s, 'http://placeholder.local')
        return isLocalhostHost(parsed.hostname)
    } catch {
        return /^https?:\/\/(localhost|127\.0\.0\.1)(?::\d+)?(?:\/|$)/i.test(s)
    }
}

export function shouldIgnoreLocalApiBaseUrl(rawBaseUrl, pageHost) {
    return Boolean(rawBaseUrl && !isLocalhostHost(pageHost) && isLocalApiBaseUrl(rawBaseUrl))
}

// env/override에 "."를 넣는 경우(동일 오리진 의도) axios의 상대경로 해석 때문에
// 현재 라우트 경로를 기준으로 붙어버린다.
// 예) /requests/new 에서 baseURL="./v1" → /requests/v1/... 로 호출됨(nginx 프록시 미적용)
// 따라서 "."/ "./"는 "루트"로 정규화한다.
function normalizeApiBaseUrl(v) {
    if (!v) return v
    const s = String(v).trim()
    if (s === '.' || s === './') return ''
    return s
}

export function resolveApiBaseUrl({ rawBaseUrl = '', pageHost = '', override = null } = {}) {
    const isLocalhost = isLocalhostHost(pageHost)

    if (override && override.length > 0) {
        const normalizedOverride = normalizeApiBaseUrl(override)
        if (shouldIgnoreLocalApiBaseUrl(normalizedOverride, pageHost)) return ''
        return normalizedOverride
    }

    if (rawBaseUrl) {
        // .env.local의 localhost 값이 운영 빌드에 섞여도 운영 브라우저가 사용자 PC의 localhost로 요청하지 않도록 방어한다.
        // 운영은 기본적으로 같은 origin의 /v1 프록시를 사용하고, 별도 API 도메인이 필요하면 실제 https URL을 주입한다.
        if (shouldIgnoreLocalApiBaseUrl(rawBaseUrl, pageHost)) return ''
        // 로컬에서 프로덕션 베이스가 주입된 빌드를 실행 중이면 localhost로 강제 전환
        if (isLocalhost && /api\.sgez\./.test(rawBaseUrl)) return 'http://localhost:8080'
        return normalizeApiBaseUrl(rawBaseUrl)
    }

    // 기본값은 "동일 호스트" 호출(상대경로)로 둔다.
    // - BE/FE를 같은 서버에서 Nginx로 프록시할 때 가장 단순/안정적 (CORS/도메인 의존 제거)
    // - 로컬 개발(webpack dev server 등)에서만 8080으로 직접 호출
    return isLocalhost ? 'http://localhost:8080' : ''
}

// API 베이스 URL 결정 로직
// 우선순위: localStorage override > 환경변수 > 호스트 기반 추론
const getBaseUrl = () => {
    const host = (typeof window !== 'undefined') ? window.location.hostname : ''
    const override = (typeof window !== 'undefined') ? localStorage.getItem('api.baseUrl.override') : null
    return resolveApiBaseUrl({ rawBaseUrl: RAW_BASE_URL, pageHost: host, override })
}

/**
 * REACT_APP_API_BASE_URL·override 에 실수로 `/v1` 까지 넣은 경우(예: http://host:8080/v1),
 * 아래에서 `${BASE}/${API_VERSION}` 를 붙이면 `/v1/v1/...` 가 되어 404(NOT_FOUND)가 난다.
 * 호스트 루트만 두고 버전은 항상 API_VERSION 한 번만 붙이도록 끝 세그먼트를 제거한다.
 */
function apiRootWithoutVersionSuffix(url) {
    if (!url) return url
    let s = String(url).trim().replace(/\/+$/, '')
    const ver = String(API_VERSION || 'v1').replace(/^\/+|\/+$/g, '')
    if (!ver) return url
    const suffix = `/${ver}`
    if (s.endsWith(suffix)) {
        s = s.slice(0, -suffix.length)
    }
    return s
}

const BASE_URL = apiRootWithoutVersionSuffix(getBaseUrl())
// 서버 API는 `/v1/...` 형태. 프론트는 API_VERSION 으로 한 번만 접두
const BASE_WITH_VERSION = `${BASE_URL.replace(/\/$/, '')}/${API_VERSION}`

/**
 * stored_files.file_id 는 `landing-images__f_…` 처럼 URL 한 경로 세그먼트에 넣을 수 있는 형태다.
 * S3 키(`landing-images/f_…`)가 필드에 들어가면 encodeURIComponent 시 %2F 가 되어 CloudFront 등에서 400이 난다.
 * @param {string|null|undefined} fileId
 * @returns {string}
 */
export function canonicalStoredFileIdForApiPath(fileId) {
    if (fileId == null) return fileId ?? ''
    const s = String(fileId).trim()
    if (!s.includes('/')) return s
    return s.replace(/\//g, '__')
}

export const api = axios.create({
    baseURL: BASE_WITH_VERSION,
    withCredentials: false, // JWT 토큰 기반으로 변경
})

// API 루트(버전 제외). 파일/이미지 등 <img src>에서 사용하기 위함.
export const API_ROOT = BASE_URL ? BASE_URL.replace(/\/$/, '') : ''

/**
 * 카카오 sendScrap 등 크롤러가 읽는 OG HTML이 있는 API 절대 URL.
 * REACT_APP_API_BASE_URL 이 있으면 BE 호스트의 /v1/... 를 쓰고, 없으면 현재 페이지 origin(동일 호스트 프록시)을 쓴다.
 * FE origin만 쓰면 API가 별도 도메인일 때 index.html이 열려 기본 og:image(로고)만 보이는 문제가 난다.
 */
function preferHttpsForKnownHosts(url) {
    if (!url) return url
    const s = String(url).trim()
    if (!s) return s
    if (typeof window !== 'undefined' && window.location?.protocol === 'https:' && s.startsWith('http://')) {
        try {
            const parsed = new URL(s, window.location.origin)
            if (/\.sgez\.(co\.kr|com)$/i.test(parsed.hostname) || parsed.hostname === window.location.hostname) {
                parsed.protocol = 'https:'
                return parsed.toString()
            }
        } catch {
            if (s.includes('sgez.co.kr') || s.includes('sgez.com')) {
                return `https://${s.slice(7)}`
            }
        }
    }
    if (s.startsWith('http://') && (s.includes('sgez.co.kr') || s.includes('sgez.com'))) {
        return `https://${s.slice(7)}`
    }
    return s
}

export function absoluteApiV1Path(pathWithoutLeadingSlash) {
    const p = String(pathWithoutLeadingSlash || '').replace(/^\//, '')
    const ver = API_VERSION
    const root = BASE_URL
        ? BASE_URL.replace(/\/$/, '')
        : (typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '')
    if (!root) return preferHttpsForKnownHosts(`/${ver}/${p}`)
    return preferHttpsForKnownHosts(`${root}/${ver}/${p}`.replace(/([^:])\/{2,}/g, '$1/'))
}

/** REACT_APP_API_BASE_URL 이 있는 환경에서 <img src>용 파일 미리보기 절대 URL */
export function absoluteApiFilePreviewUrl(fileId) {
    const id = canonicalStoredFileIdForApiPath(fileId)
    if (!id) return ''
    return absoluteApiV1Path(`files/${encodeURIComponent(id)}/preview`)
}

/**
 * 과거 코드·이름 호환용. 상대 경로(`/v1/files/…`)만 쓰면 로컬 CRA(:3000)에서 `<img src>`가 깨지므로
 * 실제 반환은 `absoluteApiFilePreviewUrl` 과 동일한 절대 URL이다.
 */
export function filePreviewRelativeUrl(fileId) {
    return absoluteApiFilePreviewUrl(fileId)
}

// 서버가 "/v1/..." 같이 상대경로로 내려주는 경우,
// API가 별도 도메인(REACT_APP_API_BASE_URL)일 때 <img>가 FE 도메인으로 잘못 요청되는 문제를 방지한다.
/**
 * 저장소 fileId를 브라우저가 그대로 쓰면 `/p/페이지코드` 기준으로 `…/p/si_xxx.png`처럼 잘못 해석될 수 있음.
 * - `profiles__si_…` / `__` 포함 키, `f_…` 레거시, 단독 `si_{uuid}(…옵션 확장자)` 는 API `/files/…/preview` 로 보낸다.
 */
function isFlatStoredFileIdForPreview(s) {
    if (!s || typeof s !== 'string') return false
    const t = s.trim()
    if (!t || t.includes('/') || t.includes('\\')) return false
    if (/__/i.test(t)) return true
    if (/^f_[a-f0-9-]+$/i.test(t)) return true
    if (/^si_[a-f0-9-]{8,}(?:\.(?:png|webp|jpe?g))?$/i.test(t)) return true
    return false
}

export function resolveApiAssetUrl(url) {
    if (!url) return url
    const s = String(url).trim()
    if (!s) return s
    if (/^(data:|blob:)/i.test(s)) return s
    if (/^https?:\/\//i.test(s)) return preferHttpsForKnownHosts(s)
    if (s.startsWith('/') && API_ROOT) return preferHttpsForKnownHosts(`${API_ROOT}${s}`)
    const canon = canonicalStoredFileIdForApiPath(s)
    if (isFlatStoredFileIdForPreview(canon)) {
        return preferHttpsForKnownHosts(`${BASE_WITH_VERSION.replace(/\/$/, '')}/files/${encodeURIComponent(canon)}/preview`)
    }
    return s
}

// 최근 서버 traceId(응답 헤더) 캐시: FE 오류 리포팅 시 함께 전송
let lastTraceId = null
const CLIENT_DEVICE_ID_STORAGE_KEY = 'auth.clientDeviceId'

export function getLastTraceId() {
    return lastTraceId
}

function sanitizeClientLogString(s, max = 2000) {
    if (!s) return s
    let v = String(s)
    // 토큰/헤더 등 마스킹(방어적)
    v = v.replace(/bearer\s+[-a-z0-9._~+/]+=*/ig, 'Bearer ***')
    v = v.replace(/(access_token|refresh_token|authorization|cookie)\s*[:=]\s*([^\s"']+)/ig, '$1=***')
    v = v.replace(/\r?\n/g, ' ')
    if (v.length > max) v = v.slice(0, max) + '…'
    return v
}

function detectClientChannel() {
    try {
        const ua = (typeof navigator !== 'undefined' && navigator.userAgent) ? String(navigator.userAgent).toLowerCase() : ''
        const isMobile = /android|iphone|ipad|ipod|mobile/i.test(ua)
        return isMobile ? 'MOBILE_WEB' : 'PC'
    } catch {
        return 'PC'
    }
}

// FE 오류/이벤트를 BE로 전송(중앙로그가 없을 때 서버 로그로 모으기)
export async function reportClientLog(payload) {
    try {
        const url = `${BASE_WITH_VERSION.replace(/\/$/, '')}/logs/client`
        const safe = {
            level: payload?.level || 'error',
            source: sanitizeClientLogString(payload?.source, 80),
            route: sanitizeClientLogString(payload?.route, 200),
            url: sanitizeClientLogString(payload?.url, 500),
            message: sanitizeClientLogString(payload?.message, 2000),
            stack: sanitizeClientLogString(payload?.stack, 8000),
            userAgent: sanitizeClientLogString(payload?.userAgent, 300),
        }
        // 인터셉터/토큰 갱신 루프를 피하기 위해 raw axios 사용
        await axios.post(url, safe, {
            headers: {
                'X-Client-Request-Id': safeRandomUUID(),
                'X-Client-Channel': detectClientChannel(),
                'X-Client-Device-Id': getOrCreateClientDeviceId(),
            },
        })
    } catch (_) {
        // ignore
    }
}

// crypto.randomUUID()는 일부 브라우저/환경(구형 Edge, 일부 WebView 등)에서 미지원일 수 있음.
// Idempotency-Key 용도로는 "충분히 유니크"하면 되므로 v4 폴백을 제공한다.
function safeRandomUUID() {
    const c = (typeof crypto !== 'undefined' && crypto) ? crypto : ((typeof window !== 'undefined' && window.crypto) ? window.crypto : null)
    if (c && typeof c.randomUUID === 'function') return c.randomUUID()

    if (c && typeof c.getRandomValues === 'function') {
        const bytes = new Uint8Array(16)
        c.getRandomValues(bytes)
        // RFC4122 v4
        bytes[6] = (bytes[6] & 0x0f) | 0x40
        bytes[8] = (bytes[8] & 0x3f) | 0x80
        const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
    }

    // 최후의 폴백(crypto 미존재): 멱등성 키로만 사용되므로 충돌 확률 낮추기
    return `idemp_${Date.now()}_${Math.random().toString(16).slice(2)}_${Math.random().toString(16).slice(2)}`
}

function stableHash(input) {
    let hash = 0x811c9dc5
    const text = String(input || '')
    for (let i = 0; i < text.length; i += 1) {
        hash ^= text.charCodeAt(i)
        hash = Math.imul(hash, 0x01000193)
    }
    return (hash >>> 0).toString(16).padStart(8, '0')
}

function webDeviceFingerprintId() {
    try {
        if (typeof window === 'undefined' || typeof navigator === 'undefined') return ''
        const screenInfo = window.screen
            ? [window.screen.width, window.screen.height, window.screen.colorDepth].join('x')
            : ''
        const timezone = (typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function')
            ? (Intl.DateTimeFormat().resolvedOptions().timeZone || '')
            : ''
        const source = [
            navigator.platform || '',
            timezone,
            screenInfo,
            navigator.maxTouchPoints || 0,
        ].join('|')
        if (!source.replace(/[|0]/g, '').trim()) return ''
        return `webfp_${stableHash(source)}_${stableHash(source.split('').reverse().join(''))}`
    } catch {
        return ''
    }
}

function getOrCreateClientDeviceId() {
    try {
        const fingerprintId = webDeviceFingerprintId()
        if (fingerprintId) return fingerprintId
        if (typeof window === 'undefined' || !window.localStorage) return ''
        const existing = localStorage.getItem(CLIENT_DEVICE_ID_STORAGE_KEY)
        if (existing && existing.trim()) return existing.trim()
        const next = safeRandomUUID()
        localStorage.setItem(CLIENT_DEVICE_ID_STORAGE_KEY, next)
        return next
    } catch {
        return ''
    }
}

// 전역 메타데이터 캐시: roles
let metaCache = { roles: null }

export async function fetchRolesMeta() {
    if (metaCache.roles) return metaCache.roles
    const res = await api.get('/meta/roles')
    const data = (res?.data && res.data.data) ? res.data.data : res.data
    metaCache.roles = Array.isArray(data) ? data : []
    return metaCache.roles
}

export async function cacheRolesMeta() {
    try {
        const roles = await fetchRolesMeta()
        if (typeof window !== 'undefined') {
            localStorage.setItem('meta.roles', JSON.stringify(roles))
        }
    } catch (_) {
        // ignore failures; UI will fall back to static labels
    }
}

export function decodeJwtPayload(token) {
    try {
        if (!token) return null
        const parts = String(token).split('.')
        if (parts.length < 2) return null
        const b64url = parts[1]
        const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/')
        const padded = b64 + '==='.slice((b64.length + 3) % 4)
        const json = atob(padded)
        return JSON.parse(json)
    } catch {
        return null
    }
}

export function getJwtExpMs(token) {
    const payload = decodeJwtPayload(token)
    const expSec = payload?.exp
    if (!expSec || typeof expSec !== 'number') return null
    return expSec * 1000
}

export function isAccessTokenExpired(token, skewMs = 5000) {
    const expMs = getJwtExpMs(token)
    if (!expMs) return false
    return Date.now() >= (expMs - skewMs)
}

// SSE 연결 유틸 (알림 스트림)
// 알림 수신을 위한 SSE 연결 (헤더 미지원 한계로 쿼리로 토큰 전달)
export function connectSse(getToken, onMessage, onDismiss) {
    const base = `${BASE_WITH_VERSION.replace(/\/$/, '')}/notifications/stream`

    let closed = false
    let es = null
    let retryMs = 1000
    const maxRetryMs = 30000
    let timer = null

    const cleanup = () => {
        if (timer) {
            clearTimeout(timer)
            timer = null
        }
        if (es) {
            try { es.close() } catch { }
            es = null
        }
    }

    const connect = async () => {
        if (closed) return
        let token = (typeof getToken === 'function') ? getToken() : null
        if (!token) return

        // 만료/임박 토큰이면 먼저 refresh 후 연결(업무시간 탭 유지 + 로그 오염 방지)
        if (isAccessTokenExpired(token, 30000)) {
            const refreshed = await refreshTokensIfPossible()
            if (!refreshed?.accessToken) {
                closed = true
                cleanup()
                return
            }
            token = refreshed.accessToken
        }
        const qs = new URLSearchParams({ access_token: token })
        es = new EventSource(`${base}?${qs.toString()}`)

        es.addEventListener('notify', (e) => {
            try { onMessage && onMessage(JSON.parse(e.data)) } catch { }
        })
        es.addEventListener('notification-dismiss', (e) => {
            try { onDismiss && onDismiss(JSON.parse(e.data)) } catch { }
        })

        es.addEventListener('ping', () => { /* keep-alive */ })
        es.addEventListener('init', () => { retryMs = 1000 })

        es.onerror = () => {
            // 토큰 만료/네트워크 오류 등 원인 구분이 어려워, 연결을 닫고 최신 토큰으로 재연결한다.
            cleanup()
            if (closed) return
            const cur = (typeof getToken === 'function') ? getToken() : null
            if (cur && isAccessTokenExpired(cur, 0)) {
                // 만료면 refresh 후 즉시 재연결 (실패하면 내부에서 로그아웃 처리)
                void (async () => {
                    const refreshed = await refreshTokensIfPossible()
                    if (!refreshed?.accessToken) {
                        closed = true
                        cleanup()
                        return
                    }
                    retryMs = 1000
                    void connect()
                })()
                return
            }
            const next = Math.min(maxRetryMs, retryMs)
            retryMs = Math.min(maxRetryMs, retryMs * 2)
            timer = setTimeout(() => { void connect() }, next)
        }
    }

    void connect()

    return {
        close: () => {
            closed = true
            cleanup()
        },
    }
}

export async function fetchNotificationInbox(size = 20) {
    const res = await api.get('/notifications/inbox', { params: { size }, __silent: true })
    return (res?.data && res.data.data !== undefined) ? res.data.data : (res?.data || {})
}

export async function fetchNotificationUnreadCount() {
    const res = await api.get('/notifications/inbox/unread-count', { __silent: true })
    return (res?.data && res.data.data !== undefined) ? res.data.data : (res?.data || {})
}

export async function markNotificationRead(id) {
    if (!id) return null
    const res = await api.patch(`/notifications/inbox/${encodeURIComponent(id)}/read`, null, { __silent: true })
    return (res?.data && res.data.data !== undefined) ? res.data.data : (res?.data || {})
}

export async function markAllNotificationsRead() {
    const res = await api.post('/notifications/inbox/read-all', null, { __silent: true })
    return (res?.data && res.data.data !== undefined) ? res.data.data : (res?.data || {})
}

export async function dismissNotification(id) {
    if (!id) return null
    const res = await api.delete(`/notifications/inbox/${encodeURIComponent(id)}`, { __silent: true })
    return (res?.data && res.data.data !== undefined) ? res.data.data : (res?.data || {})
}

export async function dismissAllNotifications() {
    const res = await api.delete('/notifications/inbox', { __silent: true })
    return (res?.data && res.data.data !== undefined) ? res.data.data : (res?.data || {})
}
// /auth 경로 판별
// /auth/* 경로 여부 판별 (로그인/리프레시/로그아웃)
const isAuthPath = (url) => {
    if (!url) return false
    // 절대/상대 경로 모두 대응
    return /(^|\/)auth\/(login|refresh|logout|social\/[^/]+\/login)(\/|$)/.test(url)
}

// 요청 인터셉터: Authorization + Idempotency-Key 주입
api.interceptors.request.use((config) => {
    config.headers = config.headers || {}

    // 모든 요청에 클라이언트 요청 ID 부여(서버 traceId와 별도로, FE 측 상관관계 용)
    if (!config.headers['X-Client-Request-Id']) {
        config.headers['X-Client-Request-Id'] = safeRandomUUID()
    }
    if (!config.headers['X-Client-Channel']) {
        config.headers['X-Client-Channel'] = detectClientChannel()
    }
    if (!config.headers['X-Client-Device-Id']) {
        const deviceId = getOrCreateClientDeviceId()
        if (deviceId) config.headers['X-Client-Device-Id'] = deviceId
    }

    // JWT 토큰 헤더 주입
    // - /auth/* 요청에는 Authorization 헤더를 붙이지 않음
    if (!isAuthPath(config.url)) {
        const token = localStorage.getItem('accessToken')
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`
        }
    }

    // 멱등성 보장을 위한 키(POST/PUT/PATCH/DELETE)에 자동 주입
    if (config.method !== 'get' && !config.headers['Idempotency-Key']) {
        config.headers['Idempotency-Key'] = safeRandomUUID()
    }

    return config
})

let refreshingPromise = null
let logoutInProgress = false
let lastRefreshFailedAt = 0
let lastRefreshFailureMessage = null
const REFRESH_FAIL_COOLDOWN_MS = 10_000

function clearAuthStorage() {
    try { localStorage.removeItem('accessToken') } catch { }
    try { localStorage.removeItem('refreshToken') } catch { }
    try { localStorage.removeItem('auth.user') } catch { }
}

function toast(message, variant = 'info', timeoutMs = 2500) {
    try {
        if (typeof window !== 'undefined' && window.toast && typeof window.toast.show === 'function') {
            window.toast.show(message, variant, timeoutMs)
        }
    } catch (_) { }
}

/**
 * axios 에러에서 사용자에게 보여줄 문자열을 만든다.
 * Spring ErrorResponse(message), ProblemDetail(title/detail), ApiResponse 등을 고려한다.
 * @param {unknown} error
 * @param {string} [fallback]
 */
function friendlyServerErrorMessage(status) {
    if (status === 502) {
        return '서버 연결이 일시적으로 불안정합니다. 잠시 후 다시 시도해 주세요.'
    }
    if (status === 503) {
        return '서비스가 잠시 사용하기 어려운 상태입니다. 잠시 후 다시 시도해 주세요.'
    }
    if (status === 504) {
        return '요청 처리 시간이 길어져 서버 응답을 받지 못했습니다. 처리가 완료됐을 수 있으니 잠시 후 화면을 새로고침해 확인해 주세요.'
    }
    if (status >= 500) {
        return '서버 처리 중 문제가 발생했습니다. 잠시 후 다시 시도해 주세요.'
    }
    return ''
}

function looksLikeProxyHtmlError(text) {
    const s = String(text || '').toLowerCase()
    return s.includes('<html')
        || s.includes('<!doctype')
        || s.includes('cloudfront')
        || s.includes('the request could not be satisfied')
        || s.includes('gateway timeout')
        || s.includes('bad gateway')
        || s.includes('service unavailable')
}

export function getApiErrorMessage(error, fallback = '요청에 실패했습니다.') {
    try {
        const status = error?.response?.status
        const data = error?.response?.data
        if (data == null || data === '') {
            if (status === 404) {
                return '요청한 API를 찾을 수 없습니다(404). 백엔드를 최신 코드로 빌드한 뒤 서버를 재시작했는지 확인해 주세요.'
            }
            const friendly = friendlyServerErrorMessage(status)
            if (friendly) return friendly
            return error?.message || fallback
        }
        if (typeof data === 'string') {
            const friendly = friendlyServerErrorMessage(status)
            if (friendly && looksLikeProxyHtmlError(data)) return friendly
            const stripped = data.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 400)
            if (friendly && stripped.length > 160) return friendly
            if (stripped) return stripped
            if (status === 404) {
                return '요청한 API를 찾을 수 없습니다(404). 백엔드를 최신 코드로 빌드한 뒤 서버를 재시작했는지 확인해 주세요.'
            }
            if (friendly) return friendly
            return fallback
        }
        const msg =
            data.message
            || data.detail
            || data.error_description
            || (typeof data.error === 'string' ? data.error : data.error?.message)
            || data.title
        if (msg) {
            const code = data.code ? String(data.code) : ''
            let out = normalizeApiErrorMessage(msg, code)
            if (code === 'SUBSCRIPTION_REQUIRED' || code === 'FEATURE_QUOTA_EXCEEDED' || code === 'FEATURE_NOT_ENABLED') {
                return out
            }
            const tid = error?.__traceId || error?.response?.headers?.['x-trace-id'] || error?.response?.headers?.['x-correlation-id']
            if (tid && status >= 500) out = `${out} (추적 ID: ${tid})`
            return out
        }
        const codeMessage = friendlyApiErrorMessageForCode(data.code)
        if (codeMessage) return codeMessage
        if (status === 404) {
            return '요청한 API를 찾을 수 없습니다(404). 백엔드를 최신 코드로 빌드한 뒤 서버를 재시작했는지 확인해 주세요.'
        }
        const friendly = friendlyServerErrorMessage(status)
        if (friendly) return friendly
        return fallback
    } catch {
        return fallback
    }
}

function apiErrorCode(error) {
    const code = error?.response?.data?.code
    return code == null ? '' : String(code).trim()
}

function isSubscriptionAccessGateApiError(error) {
    const code = apiErrorCode(error)
    if (code === 'SUBSCRIPTION_REQUIRED' || code === 'SUBSCRIPTION_EXPIRED' || code === 'SUBSCRIPTION_SUSPENDED') {
        return true
    }
    const message = String(error?.response?.data?.message || error?.response?.data?.detail || '')
    return message.includes('구독') && (message.includes('만료') || message.includes('결제') || message.includes('구독 상태'))
}

function shouldSuppressSubscriptionAccessToast(error) {
    if (!isSubscriptionAccessGateApiError(error)) return false
    return true
}

function suppressErrorPageRedirect(config) {
    if (config?.__suppressErrorPageRedirect) return true
    const url = String(config?.url || '')
    return /(^|\/)billing\/me(?:[/?#]|$)/.test(url)
}

function hasSpecificForbiddenMessage(error) {
    const status = error?.response?.status
    if (status !== 403) return false
    const code = apiErrorCode(error)
    if (code && code !== 'ACCESS_DENIED' && code !== 'FORBIDDEN') return true
    const message = getApiErrorMessage(error, '')
    return Boolean(message && message !== '접근 권한이 없습니다.' && message !== '접근이 거부되었습니다.')
}

export function triggerLogoutRedirect(message = '세션이 만료되었습니다. 다시 로그인해 주세요.') {
    if (logoutInProgress) return
    logoutInProgress = true
    try { window.dispatchEvent(new CustomEvent('auth:logout')) } catch { }
    clearAuthStorage()
    if (typeof window !== 'undefined') {
        const path = window.location?.pathname || ''
        // 로그인 만료 안내는 "이동 후"에도 보이도록 세션 플래시로 남긴다.
        try {
            sessionStorage.setItem('flash.login', JSON.stringify({
                message: message,
                variant: 'error',
            }))
        } catch { }
        // 현재 화면에서도 즉시 토스트(팝업) 표시 시도
        toast(message, 'error', 3000)
        if (path !== '/login') {
            stashCurrentPathForLoginReturn()
            window.location.href = '/login'
        }
    }
}

export async function refreshTokensIfPossible() {
    const refreshToken = localStorage.getItem('refreshToken')
    if (!refreshToken || refreshToken === 'undefined' || refreshToken === 'null') {
        // 리다이렉트는 호출하는 쪽(401 에러 처리)에서 처리
        return null
    }
    if (lastRefreshFailedAt && (Date.now() - lastRefreshFailedAt) < REFRESH_FAIL_COOLDOWN_MS) {
        return null
    }
    try {
        if (!refreshingPromise) {
            // 인터셉터 루프를 피하기 위해 raw axios 사용
            refreshingPromise = axios.post(`${BASE_WITH_VERSION}/auth/refresh`, { refreshToken }, {
                headers: {
                    'X-Client-Request-Id': safeRandomUUID(),
                    'X-Client-Channel': detectClientChannel(),
                    'X-Client-Device-Id': getOrCreateClientDeviceId(),
                },
            })
        }
        const refreshResponse = await refreshingPromise
        refreshingPromise = null

        const { accessToken, refreshToken: newRefreshToken } = (refreshResponse?.data && refreshResponse.data.data)
            ? refreshResponse.data.data
            : refreshResponse.data
        if (!accessToken || !newRefreshToken) throw new Error('invalid_refresh_response')

        localStorage.setItem('accessToken', accessToken)
        localStorage.setItem('refreshToken', newRefreshToken)
        lastRefreshFailedAt = 0
        lastRefreshFailureMessage = null
        try { window.dispatchEvent(new CustomEvent('auth:tokenRefreshed', { detail: { accessToken } })) } catch { }
        return { accessToken, refreshToken: newRefreshToken }
    } catch (error) {
        refreshingPromise = null
        lastRefreshFailedAt = Date.now()
        lastRefreshFailureMessage = getApiErrorMessage(error, '세션이 만료되었습니다. 다시 로그인해 주세요.')
        // 리다이렉트는 호출하는 쪽(401 에러 처리)에서 처리
        return null
    }
}

// 응답 인터셉터: 401 시 리프레시 후 원요청 재시도
api.interceptors.response.use(
    (res) => {
        // 서버가 내려주는 traceId를 캐시해 두었다가, FE 오류 리포팅 등에 활용
        const tid = res?.headers ? (res.headers['x-trace-id'] || res.headers['x-correlation-id']) : null
        if (tid) lastTraceId = tid
        const body = res?.data
        if (body && typeof body === 'object' && body.success === false) {
            const status = Number(body.status || res.status || 500)
            const error = new Error(getApiErrorMessage({
                response: {
                    ...res,
                    status,
                    data: body,
                },
            }))
            error.response = {
                ...res,
                status,
                data: body,
            }
            error.config = res?.config || {}
            error.__traceId = tid || undefined
            return Promise.reject(error)
        }
        return res
    },
    async (error) => {
        const original = error?.config || {}
        // 에러에도 traceId를 부착(디버깅 편의)
        try {
            const tid = error?.response?.headers ? (error.response.headers['x-trace-id'] || error.response.headers['x-correlation-id']) : null
            if (tid) {
                lastTraceId = tid
                error.__traceId = tid
            }
        } catch (_) { }

        // 401 에러 시 토큰 갱신 시도 (로그인 요청 제외), 무한루프 방지 가드
        if (error?.response?.status === 401 && !isAuthPath(original.url) && !original.__retried401) {
            // 비로그인 사용자(토큰/유저정보 없음)가 "공개 페이지"를 보고 있을 때,
            // 일부 백그라운드 요청(/meta/roles 등)이 401을 내면 무조건 /login 으로 튕기는 문제가 생길 수 있다.
            // 따라서 "인증 상태로 보이는 경우"에만 로그아웃 리다이렉트를 수행한다.
            const accessToken = localStorage.getItem('accessToken')
            const refreshToken = localStorage.getItem('refreshToken')
            const userData = localStorage.getItem('auth.user')
            const hasAnyAuthState = Boolean(
                (accessToken && accessToken !== 'undefined' && accessToken !== 'null')
                || (refreshToken && refreshToken !== 'undefined' && refreshToken !== 'null')
                || (userData && userData !== 'undefined' && userData !== 'null')
            )

            if (!hasAnyAuthState) {
                return Promise.reject(error)
            }

            const refreshed = await refreshTokensIfPossible()
            if (refreshed?.accessToken) {
                // 원래 요청 재시도 (Authorization 갱신)
                original.headers = original.headers || {}
                original.headers['Authorization'] = `Bearer ${refreshed.accessToken}`
                original.__retried401 = true
                return api.request(original)
            }
            const logoutMessage = lastRefreshFailureMessage || '세션이 만료되었습니다. 다시 로그인해 주세요.'
            // 공개 랜딩·정책 페이지: 만료 세션만 정리하고 그대로 본문을 보여준다.
            if (typeof window !== 'undefined') {
                const path = window.location?.pathname || ''
                if (isPublicAppRoute(path)) {
                    clearAuthStorage()
                    try { window.dispatchEvent(new CustomEvent('auth:logout')) } catch { }
                    return Promise.reject(error)
                }
                if (path !== '/unauthorized' && path !== '/login') {
                    try {
                        sessionStorage.setItem('flash.login', JSON.stringify({
                            message: logoutMessage,
                            variant: 'error',
                        }))
                    } catch { }
                    toast(logoutMessage, 'error', 3000)
                    stashCurrentPathForLoginReturn()
                    window.location.href = '/unauthorized'
                }
            }
            return Promise.reject(error)
        }

        // 기타 에러는 토스트로 한 번 안내(너무 시끄럽지 않게 짧게)
        try {
            // 특정 요청은 화면에서 자체적으로 에러를 처리하므로(인라인 저장 등),
            // 전역 토스트를 띄우지 않도록 옵션을 제공한다.
            const silent = Boolean(original?.__silent)
            const status = error?.response?.status
            const suppressSubscriptionAccessToast = shouldSuppressSubscriptionAccessToast(error)

            // __silent 요청도 409(CONFLICT)는 서버 메시지를 토스트로 노출 (고객 PATCH 반영 등)
            if (silent && status === 409 && !original.__suppressGlobal409Toast) {
                toast(getApiErrorMessage(error, '처리할 수 없습니다.'), 'error', 5200)
                return Promise.reject(error)
            }

            // 401 에러는 토큰 갱신 실패 시 세션 만료로 처리 (이미 위에서 처리됨)
            if (!silent && status === 401 && original.__retried401) {
                // 토큰 갱신 실패 후 401 에러 - 세션 만료 메시지 표시
                toast('세션이 만료되었습니다. 다시 로그인해 주세요.', 'error', 3000)
                return Promise.reject(error)
            }
            
            if (!silent && status && status !== 401) {
                // 에러 코드별 페이지로 리다이렉트
                if (typeof window !== 'undefined') {
                    const path = window.location?.pathname || ''
                    let redirectPath = null
                    
                    if (status === 400) {
                        // 입력값 검증 실패 등 사용자 조작으로 발생한 400은 현재 화면에 남겨 토스트로 안내한다.
                        redirectPath = null
                    } else if (status === 403) {
                        const reqUrl = String(original?.url || '')
                        if (isBillingGateApiError(error) || hasSpecificForbiddenMessage(error)) {
                            redirectPath = null
                        } else {
                            redirectPath = (reqUrl.includes('claims/form-templates') || reqUrl.includes('claims/additional-fields')) ? null : '/forbidden'
                        }
                    } else if (status === 402) {
                        redirectPath = null
                    } else if (status === 404) {
                        redirectPath = null // 404는 라우터에서 처리
                    } else if (status === 500) {
                        redirectPath = '/internal-server-error'
                    } else if (status === 502) {
                        redirectPath = '/bad-gateway'
                    } else if (status === 503) {
                        redirectPath = '/service-unavailable'
                    } else if (status === 504) {
                        redirectPath = '/gateway-timeout'
                    }
                    
                    // 리다이렉트가 필요한 경우
                    if (redirectPath && !suppressErrorPageRedirect(original) && path !== redirectPath && !path.startsWith('/login') && !path.startsWith('/unauthorized')) {
                        window.location.href = redirectPath
                        return Promise.reject(error)
                    }
                }
                
                // 리다이렉트하지 않는 경우 토스트 메시지 표시
                if (status === 403) {
                    if (isBillingGateApiError(error)) {
                        if (suppressSubscriptionAccessToast) return Promise.reject(error)
                        toast(getApiErrorMessage(error, '이용할 수 없습니다.'), 'error', 4200)
                    } else {
                        toast(getApiErrorMessage(error, '접근 권한이 없습니다.'), 'error', 3000)
                    }
                } else if (status === 402) {
                    if (suppressSubscriptionAccessToast) return Promise.reject(error)
                    toast(getApiErrorMessage(error, '이번 달 사용 가능 횟수를 모두 사용했습니다.'), 'error', 4200)
                } else if (status === 409) {
                    toast(getApiErrorMessage(error, '처리할 수 없습니다.'), 'error', 5200)
                } else if (status === 400) {
                    toast(getApiErrorMessage(error, '입력값을 확인해 주세요.'), 'error', 4200)
                } else if (status >= 500) {
                    toast(getApiErrorMessage(error, `서버 오류가 발생했습니다. (${status})`), 'error', 4200)
                } else if (status !== 400 && status !== 404 && status !== 409) {
                    toast(getApiErrorMessage(error, `요청 처리 중 오류가 발생했습니다. (${status})`), 'error', 3800)
                }
            }
        } catch (_) { }
        return Promise.reject(error)
    }
)
